import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DonutCard, COLORS } from "@/components/charts/DashboardCharts";
import { Badge } from "@/components/ui/badge";
import { Users, ShieldAlert, KeyRound, Activity, UserCog, Sparkles } from "lucide-react";
import { formatDateTimeID } from "@/lib/format";

function StatCard({ title, value, icon: Icon, hint, accent = "slate", testId }) {
  const bg = { slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-800", rose: "bg-rose-100 text-rose-700" }[accent];
  return (
    <Card data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-slate-500">{title}</div>
            <div className="font-mono-num text-3xl font-bold mt-2">{value}</div>
            {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
          </div>
          <div className={`h-10 w-10 rounded-lg grid place-items-center ${bg}`}><Icon className="h-5 w-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dasbor</h1>
          <p className="text-slate-500 mt-2">Selamat datang, <span className="font-semibold text-slate-800">{user?.full_name}</span>. Berikut ringkasan sistem hari ini.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-slate-900 hover:bg-slate-900">{user?.role_name}</Badge>
          <span className="text-xs text-slate-500">Terakhir masuk: {formatDateTimeID(user?.last_login_at)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Pengguna Aktif" value={stats?.total_active_users ?? "—"} icon={Users} accent="emerald" testId="stat-active-users" />
        <StatCard title="Pengguna Tidak Aktif" value={stats?.total_inactive_users ?? "—"} icon={UserCog} accent="slate" testId="stat-inactive-users" />
        <StatCard title="Sesi Aktif" value={stats?.active_sessions ?? "—"} icon={Activity} accent="emerald" testId="stat-active-sessions" />
        <StatCard title="Jumlah Peran" value={stats?.total_roles ?? "—"} icon={KeyRound} accent="slate" testId="stat-total-roles" />
      </div>

      {/* Executive chart summary (Phase 1 — recharts, no new dependency) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard
          title="Komposisi Pengguna"
          testId="chart-user-composition"
          hint="Belum ada data pengguna."
          data={stats ? [
            { name: "Aktif",            value: stats.total_active_users   || 0, color: COLORS.positive },
            { name: "Tidak Aktif",      value: stats.total_inactive_users || 0, color: COLORS.muted },
            { name: "Terkunci",         value: stats.total_locked_users   || 0, color: COLORS.risk },
          ] : null}
        />
        <DonutCard
          title="Peringatan Keamanan"
          testId="chart-security"
          hint="Tidak ada percobaan gagal / sesi terkunci."
          data={stats ? [
            { name: "Gagal Masuk",      value: stats.failed_logins       || 0, color: COLORS.warning },
            { name: "Terkunci",         value: stats.total_locked_users  || 0, color: COLORS.risk },
            { name: "Sesi Aktif",       value: stats.active_sessions     || 0, color: COLORS.baseline },
          ] : null}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display">Aktivitas Sistem Terbaru</CardTitle></CardHeader>
          <CardContent>
            {stats?.recent_activities?.length ? (
              <div className="divide-y divide-slate-100">
                {stats.recent_activities.map((a) => (
                  <div key={a.id} className="py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">{a.activity}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {a.user_name || "Sistem"} • {a.module}{a.reference ? ` • ${a.reference}` : ""}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">{formatDateTimeID(a.timestamp)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">Belum ada aktivitas.</div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="font-display">Peringatan Keamanan</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4 text-amber-600" /> Percobaan gagal masuk</div>
                <div className="font-mono-num font-semibold">{stats?.failed_logins ?? 0}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4 text-rose-600" /> Pengguna terkunci/ditangguhkan</div>
                <div className="font-mono-num font-semibold">{stats?.total_locked_users ?? 0}</div>
              </div>
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">Status Konfigurasi Sistem: <span className="text-emerald-700 font-medium">Baik</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /> Segera Hadir</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">Modul Keuangan, Penjualan, Pembelian, Akuntansi, Perpajakan, dan Laporan akan tersedia pada tahap pengembangan berikutnya.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
